<template>
  <div :class="{ active: opened.length > 0 }" class="side-box">
    <span class="side-box__overlay" />
    <div
      :class="{
        opened: opened.indexOf('moveDevice') >= 0,
        'last-active': opened.indexOf('moveDevice') == opened.length - 1
      }"
      class="side-box__inner"
      style="z-index: 0"
    >
      <div class="side-box__header d-flex justify-content-between align-items-center">
        <div class="atom-back atom-back--gray" @click="$emit('closeBox', 'moveDevice')">
          <div class="atom-back__inner d-flex align-items-center">
            <img src="@/assets/icons/arrows/arr-gray-left.svg" />
            <span>{{ $t("zpet") }}</span>
          </div>
        </div>
        <button :disabled="!isFormValid" class="btn btn-primary" @click="handleMoveDevice">
          <div class="atom-icon">
            <img src="@/assets/icons/actions/ico-btn-ulozit.svg" />
          </div>
          {{ $t("MOVE_DEVICE") }}
        </button>
      </div>
      <div v-if="devices.length === 1" class="side-box__title">
        <h2>{{ devices[0].custDeviceName }}</h2>
      </div>
      <div v-else>
        <h2>{{ $t("MOVING") }} {{ devices.length }} {{ $t("MOVING_DEVICES") }}</h2>
      </div>
      <div v-if="devices.length > 100" class="text-danger text-larger text-center mt-5">
        {{ $t("MOVE_LIMIT_REACHED") }}
      </div>
      <div v-else class="side-box__content">
        <FormSelect
          :items="customerStore.customerList"
          :value="selectedCustomer"
          item-text="customerName"
          item-value="customerId"
          label="Zákazník"
          @change="selectedCustomer = $event"
        />
        <FormSelect
          v-if="selectedCustomer"
          :items="projectsForSelectedCustomer"
          :value="selectedProject"
          item-text="projectName"
          item-value="projectId"
          label="Projekt"
          @change="selectedProject = $event"
        />
        <FormSelect
          v-if="selectedProject"
          :items="groupsForSelectedProject"
          :value="selectedDataFlow"
          item-text="deviceGroupName"
          item-value="deviceGroupId"
          label="Datový tok"
          @change="selectedDataFlow = $event"
        />
        <FormSelect
          v-if="selectedProject"
          :items="validServices"
          :value="selectedService"
          item-subtext="custServiceId"
          item-text="custServiceName"
          item-value="custServiceId"
          label="Služba"
          @change="selectedService = $event"
        />
        <ServiceLimits v-if="selectedService" :service="selectedService" />
      </div>
    </div>
    <div v-if="errorsToTranslate.length > 0">
      <ErrorModal :centered="true" :errors="errorsToTranslate" :opened="openedError" @close="openedError = false" />
    </div>
  </div>
</template>

<script>
import FormSelect from "@/components/forms/FormSelect.vue"
import { mapActions, mapMutations, mapState } from "vuex"
import axios from "axios"
import ServiceLimits from "@/components/boxes/serviceLimits.vue"
import ErrorModal from "@/components/confirmations/translatedErrors.vue"
import { useCustomerStore } from "@/store/modules/customer"
import { useProjectStore } from "@/store/modules/project"
import { useAuthStore } from "@/store/modules/auth"
import { useLoraStore } from "@/store/modules/devices/lora"
import { useMqttStore } from "@/store/modules/devices/mqtt"
import { useGroupsStore } from "@/store/modules/groups"

export default {
  components: {
    ErrorModal,
    ServiceLimits,
    FormSelect
  },
  props: {
    tags: Array,
    reloadAttributes: Boolean,
    opened: Array,
    devices: Array
  },
  setup() {
    const customerStore = useCustomerStore()
    const projectStore = useProjectStore()
    const authStore = useAuthStore()
    const loraStore = useLoraStore()
    const mqttStore = useMqttStore()
    const groupsStore = useGroupsStore()

    return { customerStore, projectStore, authStore, loraStore, mqttStore, groupsStore }
  },
  data: () => ({
    allProjects: [],
    projectsForSelectedCustomer: [],
    errorsToTranslate: [],
    openedError: false,
    selectedCustomer: null,
    selectedDataFlow: null,
    selectedProject: null,
    selectedService: null
  }),
  computed: {
    ...mapState({
      services: (state) => state.services.services
    }),
    allGroups() {
      return this.groupsStore.allGroups
    },
    groupsForSelectedProject() {
      return this.allGroups.filter(
        (group) => group.projectId === this.selectedProject?.projectId && group.technology === this.deviceTechnology
      )
    },
    project() {
      return this.projectStore.selectedProject
    },
    apiUser() {
      return this.authStore.apiUser
    },
    validServices() {
      // this.services can be an Array or an empty Object
      try {
        return this.services.filter((service) => !service.suspended && !service.discontinued)
      } catch (e) {
        return []
      }
    },
    isFormValid() {
      return (
        this.selectedCustomer &&
        this.selectedProject &&
        this.selectedDataFlow &&
        this.selectedService &&
        this.devices.length <= 100
      )
    },
    deviceTechnology() {
      const pageName = this.$router.currentRoute.value.name
      if (pageName === "lora" || pageName === "device") {
        return "LORA"
      } else {
        return "MQTT"
      }
    }
  },
  mounted() {
    this.onOpen()
  },
  watch: {
    async selectedCustomer() {
      this.selectedProject = null
      this.selectedDataFlow = null
      this.selectedService = null
      await this.getProjectsForCustomer()
      if (this.projectsForSelectedCustomer.length === 1) {
        this.selectedProject = this.projectsForSelectedCustomer[0]
      }
    },
    selectedProject() {
      this.selectedDataFlow = null
      this.selectedService = null
      if (this.groupsForSelectedProject.length === 1) {
        this.selectedDataFlow = this.groupsForSelectedProject[0]
      }
      if (this.selectedProject) {
        this.getServices(this.selectedProject?.projectId)
      }
    },
    validServices() {
      this.selectedService = null
      if (this.validServices.length === 1 && this.selectedProject) {
        this.selectedService = this.validServices[0]
      }
    }
  },
  methods: {
    ...mapActions({
      getServices: "services/getServices"
    }),
    async getAllGroups() {
      return this.groupsStore.getAllGroups()
    },
    ...mapMutations({
      setWaitingLoader: "modals/setWaitingLoader",
      setNotification: "modals/setGlobalNotification"
    }),
    async getCustomers() {
      return await this.customerStore.getCustomerList()
    },
    async onOpen() {
      await this.getAllProjects()
      if (this.allProjects.length === 1) {
        this.errorsToTranslate.push({ code: 400, errors: [this.$t("ERROR_ONLY_ONE_PROJECT")], status: "error" })
        this.openedError = true
      }

      this.getCustomers()
      this.getAllGroups()
      if (this.customerStore.customerList.length === 1) {
        this.selectedCustomer = this.customerStore.customerList[0]
      }
    },
    async getAllProjects() {
      const url = `${process.env.VUE_APP_API}/projects?sort=%2BprojectName&limit=1000&t=`
      try {
        const res = await axios.get(url)
        this.allProjects = res.data.data
      } catch (e) {
        console.error(e)
      }
    },
    async getProjectsForCustomer() {
      const url = `${process.env.VUE_APP_API}/projects?sort=%2BprojectName&limit=1000&customerId[eq]=${this.selectedCustomer.customerId}`
      try {
        const res = await axios.get(url)
        this.projectsForSelectedCustomer = res.data.data
      } catch (e) {
        console.error(e)
      }
    },
    async handleMoveDevice() {
      this.setWaitingLoader(this.$t("MOVING_DEVICE_WAITING"))
      this.errorsToTranslate = []
      try {
        const data = {
          deviceIds: this.devices.map((device) => device.deviceId),
          dest_custServiceId: this.selectedService.custServiceId,
          dest_deviceGroup: this.selectedDataFlow.deviceGroupId,
          dest_projectId: this.selectedProject.projectId
        }

        if (this.deviceTechnology === "LORA") {
          await this.loraStore.moveDevices(data)
        } else {
          await this.mqttStore.moveDevices(data)
        }

        this.$emit("closeBox", "moveDevice")
        this.$emit("deviceMoved")
        if (this.devices.length === 1) {
          this.setNotification({
            text: "NOTIFICATION_DEVICE_MOVED",
            error: false,
            params: {
              deviceId: this.devices[0].deviceId
            }
          })
        } else {
          this.setNotification({
            text: "NOTIFICATION_DEVICES_MOVED",
            error: false,
            params: {
              devicesCount: this.devices.length
            }
          })
        }
      } catch (e) {
        this.errorsToTranslate.push(e.data)
      }

      this.setWaitingLoader()
      if (this.errorsToTranslate.length > 0) {
        this.openedError = true
      }
    }
  }
}
</script>
